import type { ProjectDetail, WikiPageSummary } from "@esi/shared";
import { ArrowRight, Folder, ListFilter, X } from "lucide-react";
import { useId } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { formatDate } from "../i18n/format.ts";
import { can } from "../projects/capabilities.ts";
import { useFolders } from "../projects/folders.ts";
import { Link, navigate, wikiChangesPath, wikiPath, wikiTabPath } from "../router.tsx";
import { useChangesInbox } from "./queries.ts";

/** How many of the most recently updated pages the overview lists. */
export const RECENT_PAGES = 8;

/**
 * The Wiki tab without a page in its URL (spec `project-wiki` "Wiki overview"): how many pages
 * the viewer can read and the most recently updated of them, then for those who may review, what
 * waits for them. Built only from the server's reader-filtered index and review queue, so it
 * neither counts nor names a page the viewer cannot read.
 */
export function WikiOverview(props: { project: ProjectDetail; pages: readonly WikiPageSummary[] }) {
  const { project, pages } = props;
  const { t } = useTranslation();
  const titleId = useId();
  const recentId = useId();
  // The tree's folder list (same query): a page names its folder, never its path.
  const folders = useFolders(project.id).data?.folders;
  const folderName = new Map(folders?.map((f) => [f.id, f.name]));
  const recent = [...pages]
    .sort((a, b) => Date.parse(b.updatedAt) - Date.parse(a.updatedAt))
    .slice(0, RECENT_PAGES);
  return (
    <section data-esi-content="wiki" aria-labelledby={titleId} className="flex flex-col gap-8">
      <header className="flex flex-col gap-1">
        <h2 id={titleId} className="text-2xl font-semibold tracking-tight text-fg">
          {t("wiki.overview.title")}
        </h2>
        <p className="text-sm text-fg-secondary tabular-nums">
          {t("wiki.overview.pages", { count: pages.length })}
        </p>
      </header>
      {can(project, "wiki.view_drafts") ? <ReviewSummary project={project} /> : null}
      <section aria-labelledby={recentId} className="flex flex-col gap-2">
        <h3 id={recentId} className="text-sm font-semibold text-fg">
          {t("wiki.overview.recent")}
        </h3>
        <ul className="divide-y divide-line rounded-lg border border-line">
          {recent.map((page) => {
            const folder = page.folderId ? folderName.get(page.folderId) : undefined;
            return (
              <li key={page.id} className="flex flex-col gap-1 px-4 py-3">
                <Link
                  href={wikiPath(project.id, page.id)}
                  className="text-sm font-medium text-fg underline-offset-4 hover:underline"
                >
                  {page.title}
                </Link>
                <p className="flex flex-wrap items-center gap-x-1.5 text-xs text-fg-muted">
                  {folder ? (
                    <>
                      <span className="inline-flex min-w-0 items-center gap-1">
                        <Folder aria-hidden="true" className="size-3 shrink-0" strokeWidth={1.75} />
                        <span className="break-words">{folder}</span>
                      </span>
                      <span aria-hidden="true">·</span>
                    </>
                  ) : null}
                  <span>{t("wiki.overview.updated", { date: formatDate(page.updatedAt) })}</span>
                  {page.status === "ready" ? null : (
                    <>
                      <span aria-hidden="true">·</span>
                      <span>{t(`wiki.overview.status.${page.status}`)}</span>
                    </>
                  )}
                </p>
              </li>
            );
          })}
        </ul>
      </section>
    </section>
  );
}

/** What waits for review, counted as the toolbar counts it, with the way to Review and Activity. */
function ReviewSummary({ project }: { project: ProjectDetail }) {
  const { t } = useTranslation();
  // The toolbar's count (change sets plus review items); named as changes while only change sets
  // wait, as items to review once another kind of item waits too (as the empty wiki says it).
  const { queue, inbox } = useChangesInbox(project);
  const needs = inbox?.toDecide;
  const changes = needs?.changeSets.length ?? 0;
  const count = needs ? changes + needs.items.length : undefined;
  return (
    <section
      aria-label={t("wiki.overview.review")}
      className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 rounded-lg border border-line bg-surface-raised px-4 py-3 text-sm"
    >
      <p className={queue.isError && !needs ? "text-danger-fg" : "text-fg"}>
        {queue.isError && !needs
          ? queue.error.message
          : count === undefined
            ? " "
            : count === 0
              ? t("wiki.overview.nothingWaiting")
              : count === changes
                ? t("wiki.overview.waiting", { count })
                : t("wiki.reviewCount", { count })}
      </p>
      <div className="flex flex-wrap items-center gap-4">
        <Link
          href={wikiChangesPath(project.id)}
          className="inline-flex items-center gap-1 text-accent-text underline-offset-4 hover:underline"
        >
          {t("wiki.changes.review")}
          <ArrowRight aria-hidden="true" className="size-3.5" strokeWidth={1.75} />
        </Link>
        <Link
          href={wikiTabPath(project.id, "activity")}
          className="inline-flex items-center gap-1 text-fg-secondary underline-offset-4 hover:text-fg hover:underline"
        >
          {t("wiki.changes.activity")}
          <ArrowRight aria-hidden="true" className="size-3.5" strokeWidth={1.75} />
        </Link>
      </div>
    </section>
  );
}

/** `?filter=needs-checking` on the Wiki tab (from Overview's Need checking figure). */
export const NEEDS_CHECKING_FILTER = "needs-checking";

/**
 * The Wiki tab's pages that need checking (spec `project-home` "Wiki pages that need checking"):
 * the pages of the reader-filtered index the server marked stale or unverified for this viewer,
 * each with its reason, the oldest update first. It narrows what the server returned; it never
 * reveals anything.
 */
export function WikiNeedsChecking(props: {
  project: ProjectDetail;
  pages: readonly WikiPageSummary[];
}) {
  const { project } = props;
  const { t } = useTranslation();
  const titleId = useId();
  const pages = props.pages
    .filter((p) => p.check !== undefined)
    .sort((a, b) => Date.parse(a.updatedAt) - Date.parse(b.updatedAt));
  return (
    <section aria-labelledby={titleId} className="flex flex-col gap-4">
      <header className="flex flex-col gap-1">
        <h2 id={titleId} className="text-2xl font-semibold tracking-tight text-fg">
          {t("wiki.needsChecking.title")}
          <span className="ml-2 text-base font-normal text-fg-muted tabular-nums">
            {pages.length}
          </span>
        </h2>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <p role="status" className="inline-flex items-center gap-1.5 text-sm text-fg-secondary">
            <ListFilter aria-hidden="true" className="size-4 shrink-0" strokeWidth={1.75} />
            {t("wiki.needsChecking.filter")}
          </p>
          <Button
            size="sm"
            variant="ghost"
            className="-ml-2"
            onClick={() => navigate(wikiPath(project.id), { replace: true })}
          >
            <X aria-hidden="true" strokeWidth={1.75} />
            {t("wiki.needsChecking.clear")}
          </Button>
        </div>
      </header>
      {pages.length === 0 ? (
        <p className="text-sm text-fg-secondary">{t("wiki.needsChecking.none")}</p>
      ) : (
        <ul
          aria-labelledby={titleId}
          className="divide-y divide-line rounded-lg border border-line"
        >
          {pages.map((page) => (
            <li key={page.id} className="flex flex-col gap-1 px-4 py-3">
              <Link
                href={wikiPath(project.id, page.id)}
                className="text-sm font-medium text-fg underline-offset-4 hover:underline"
              >
                {page.title}
              </Link>
              <p className="flex flex-wrap items-center gap-x-1.5 text-xs text-fg-muted">
                <span className="inline-flex items-center gap-1.5 text-warning-fg">
                  <span aria-hidden="true" className="size-1.5 rounded-full bg-warning-border" />
                  {page.check ? t(`wiki.needsChecking.reason.${page.check}`) : null}
                </span>
                <span aria-hidden="true">·</span>
                <span>{t("wiki.overview.updated", { date: formatDate(page.updatedAt) })}</span>
              </p>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
